import { execFile } from "node:child_process";
import { createRequire } from "node:module";
import fs from "node:fs/promises";
import path from "node:path";
import { pathToFileURL } from "node:url";
import { isDeepStrictEqual, promisify } from "node:util";
import type { NoriPlugin, NoriPluginApiV1, NoriPluginManifest } from "@mewbleh/nori-plugin-sdk";
import type { NoriPaths, SharedStore } from "@mewbleh/nori-persistence";
import { satisfies, valid } from "semver";
import type { ProviderRegistry } from "./runtime.js";

const execFileAsync = promisify(execFile);
const NORI_VERSION = "0.3.0";

export type ChannelRegistrar = { register(channel: Parameters<NoriPluginApiV1["registerChannel"]>[0]): void };

function validateManifest(value: unknown): asserts value is NoriPluginManifest {
  if (!value || typeof value !== "object") throw new Error("Plugin must export a manifest");
  const manifest = value as Record<string, unknown>;
  if (!/^[a-z0-9][a-z0-9._-]{1,63}$/u.test(String(manifest.id ?? ""))) throw new Error("Plugin manifest has an invalid id");
  if (manifest.apiVersion !== 1) throw new Error(`Unsupported plugin API version: ${String(manifest.apiVersion)}`);
  if (typeof manifest.noriVersion !== "string" || !manifest.noriVersion.trim()) throw new Error("Plugin manifest must declare a Nori version range");
  if (!Array.isArray(manifest.kinds) || !Array.isArray(manifest.permissions)) throw new Error("Plugin kinds and permissions must be arrays");
  const kinds = new Set(["provider", "channel", "tool", "memory", "hook"]);
  if (manifest.kinds.some((kind) => typeof kind !== "string" || !kinds.has(kind))) throw new Error("Plugin manifest contains an unsupported kind");
  if (manifest.permissions.some((permission) => typeof permission !== "string" || !permission)) throw new Error("Plugin permissions must be non-empty strings");
}

function pluginFromModule(module: Record<string, unknown>): NoriPlugin {
  const candidate = (module.plugin ?? module.default ?? module) as Partial<NoriPlugin>;
  validateManifest(candidate.manifest);
  if (typeof candidate.register !== "function") throw new Error("Plugin must export register(api)");
  return candidate as NoriPlugin;
}

function packageNameFromSpec(spec: string): string {
  const versionMarker = spec.lastIndexOf("@");
  if (versionMarker <= 0) return spec;
  if (spec.startsWith("@") && versionMarker < spec.indexOf("/")) return spec;
  return spec.slice(0, versionMarker);
}

function validatePackageSpec(spec: string): string {
  if (!/^(?:@[a-z0-9._-]+\/)?[a-z0-9._-]+(?:@[a-z0-9._-]+)?$/iu.test(spec)) {
    throw new Error("Plugin package must be an npm package name with an optional exact version");
  }
  const packageName = packageNameFromSpec(spec);
  const version = spec.length > packageName.length ? spec.slice(packageName.length + 1) : undefined;
  if (version && !valid(version)) throw new Error("Plugin versions must be exact semantic versions, not tags or ranges");
  return packageName;
}

export class PluginManager {
  constructor(
    readonly paths: NoriPaths,
    readonly store: SharedStore,
    readonly providers: ProviderRegistry,
    readonly channels: ChannelRegistrar,
  ) {}

  async #readManifest(packageName: string): Promise<NoriPluginManifest> {
    packageName = validatePackageSpec(packageName);
    const packageJsonPath = path.join(this.paths.pluginsDir, "node_modules", ...packageName.split("/"), "package.json");
    const packageJson = JSON.parse(await fs.readFile(packageJsonPath, "utf8")) as Record<string, unknown>;
    const manifest = packageJson.nori;
    validateManifest(manifest);
    if (!satisfies(NORI_VERSION, manifest.noriVersion, { includePrerelease: true })) {
      throw new Error(`Plugin ${manifest.id} requires Nori ${manifest.noriVersion}; installed Nori is ${NORI_VERSION}`);
    }
    return manifest;
  }

  async #loadPackage(packageName: string): Promise<NoriPlugin> {
    const declaredManifest = await this.#readManifest(packageName);
    const packageJson = path.join(this.paths.pluginsDir, "package.json");
    try { await fs.access(packageJson); }
    catch { await fs.writeFile(packageJson, '{"private":true,"type":"module"}\n', { mode: 0o600 }); }
    const require = createRequire(packageJson);
    const entry = require.resolve(packageName);
    const module = (await import(`${pathToFileURL(entry).href}?nori=${Date.now()}`)) as Record<string, unknown>;
    const plugin = pluginFromModule(module);
    if (!isDeepStrictEqual(plugin.manifest, declaredManifest)) throw new Error(`Plugin ${packageName} export does not match its static package.json nori manifest`);
    return plugin;
  }

  async inspect(packageName: string): Promise<NoriPluginManifest> {
    return this.#readManifest(validatePackageSpec(packageName));
  }

  async install(packageName: string, enable = false): Promise<NoriPluginManifest> {
    const installedPackageName = validatePackageSpec(packageName);
    await fs.mkdir(this.paths.pluginsDir, { recursive: true, mode: 0o700 });
    await execFileAsync("npm", [
      "install",
      "--ignore-scripts",
      "--omit=dev",
      "--no-audit",
      "--no-fund",
      "--no-save",
      "--prefix",
      this.paths.pluginsDir,
      packageName,
    ], { timeout: 5 * 60_000, maxBuffer: 2 * 1024 * 1024 });
    let manifest: NoriPluginManifest;
    try {
      manifest = await this.#readManifest(installedPackageName);
    } catch (error) {
      await execFileAsync("npm", ["uninstall", "--ignore-scripts", "--no-audit", "--no-fund", "--prefix", this.paths.pluginsDir, installedPackageName], { timeout: 5 * 60_000, maxBuffer: 2 * 1024 * 1024 }).catch(() => undefined);
      throw error;
    }
    this.store.savePlugin(packageName, manifest as unknown as Record<string, unknown>, enable);
    if (enable) await this.register(await this.#loadPackage(installedPackageName));
    return manifest;
  }

  async register(plugin: NoriPlugin): Promise<void> {
    validateManifest(plugin.manifest);
    const api: NoriPluginApiV1 = {
      registerProvider: (provider) => this.providers.register(provider),
      registerChannel: (channel) => this.channels.register(channel),
    };
    await plugin.register(api);
  }

  async loadEnabled(allow: readonly string[]): Promise<string[]> {
    const loaded: string[] = [];
    for (const record of this.store.listPlugins()) {
      if (!record.enabled || (!allow.includes(record.id) && !allow.includes(record.packageName))) continue;
      const plugin = await this.#loadPackage(packageNameFromSpec(record.packageName));
      await this.register(plugin);
      loaded.push(record.id);
    }
    return loaded;
  }

  async uninstall(id: string): Promise<boolean> {
    const record = this.store.listPlugins().find((entry) => entry.id === id);
    if (!record) return false;
    await execFileAsync("npm", [
      "uninstall",
      "--ignore-scripts",
      "--no-audit",
      "--no-fund",
      "--prefix",
      this.paths.pluginsDir,
      validatePackageSpec(record.packageName),
    ], { timeout: 5 * 60_000, maxBuffer: 2 * 1024 * 1024 });
    return this.store.removePlugin(id);
  }
}
